import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import API_BASE_URL from '../config/api';

const PostingStatusPage = () => {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/submissions/${submissionId}/status`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch submission status');
      }

      const result = await response.json();
      setStatus(result.data);
      setLoading(false);
    } catch (err) {
      console.error('Status fetch error:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Fetch status once on mount - no polling
    fetchStatus();
  }, [submissionId]);

  const getTierTimeframe = () => {
    if (!status || !status.tier_id) return 'soon';
    
    switch (status.tier_id) {
      case 1:
        return 'instantly';
      case 2:
        return 'within 24 hours';
      case 3:
        return 'within 3 days';
      case 4:
        return 'within 1-2 weeks';
      default:
        return 'soon';
    }
  };

  const getStatusMessage = () => {
    if (!status) return { severity: 'info', text: 'Loading status...' };

    // Check if payment is completed (either paid or promo code used)
    const isPaymentComplete = status.payment_status === 'paid' || status.payment_status === 'promo' || status.payment_status === 'completed';
    
    if (!isPaymentComplete) {
      return { severity: 'warn', text: 'Payment pending...' };
    }

    const timeframe = getTierTimeframe();

    switch (status.posting_status) {
      case 'not_queued':
      case 'pending':
      case 'queued':
        return { 
          severity: 'success', 
          text: `Payment confirmed! Your post will go live ${timeframe}.` 
        };
      case 'processing':
      case 'posting':
        return { severity: 'info', text: 'Your post is being published to Instagram now...' };
      case 'completed':
        return { severity: 'success', text: 'Successfully posted to Instagram!' };
      case 'failed':
        return { severity: 'error', text: 'Posting failed. Please DM @UniPostings on Instagram for support.' };
      default:
        return { severity: 'info', text: `Your post will go live ${timeframe}.` };
    }
  };

  const getStatusIcon = () => {
    if (!status) return 'pi pi-spin pi-spinner';

    // Check if payment is completed (either paid or promo code used)
    const isPaymentComplete = status.payment_status === 'paid' || status.payment_status === 'promo' || status.payment_status === 'completed';
    
    if (!isPaymentComplete) {
      return 'pi pi-clock';
    }

    switch (status.posting_status) {
      case 'completed':
        return 'pi pi-check-circle';
      case 'failed':
        return 'pi pi-times-circle';
      case 'processing':
      case 'posting':
        return 'pi pi-spin pi-spinner';
      default:
        return 'pi pi-check-circle'; // Show success icon for queued posts
    }
  };

  const statusMessage = getStatusMessage();
  const statusIcon = getStatusIcon();

  if (error) {
    return (
      <div className="posting-status-page">
        <div className="container" style={{ maxWidth: '600px', margin: '50px auto', padding: '20px' }}>
          <Card>
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <i className="pi pi-exclamation-triangle" style={{ fontSize: '4rem', color: '#f44336', marginBottom: '20px' }}></i>
              <h2>Error Loading Status</h2>
              <p style={{ marginBottom: '10px' }}>{error}</p>
              <p style={{ fontSize: '0.95rem', color: '#666', marginBottom: '20px' }}>
                If you need assistance, please DM @UniPostings on Instagram.
              </p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button 
                  label="DM @UniPostings" 
                  icon="pi pi-instagram"
                  onClick={() => window.open('https://www.instagram.com/unipostings/', '_blank')}
                  className="p-button-help"
                />
                <Button 
                  label="Go Home" 
                  icon="pi pi-home"
                  onClick={() => navigate('/')}
                  className="p-button-secondary"
                />
              </div>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="posting-status-page">
      <div className="container" style={{ maxWidth: '600px', margin: '50px auto', padding: '20px' }}>
        <Card>
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            {/* Status Icon/Spinner */}
            {(status?.posting_status === 'processing' || status?.posting_status === 'posting') ? (
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                <ProgressSpinner 
                  style={{ width: '80px', height: '80px' }}
                  strokeWidth="4"
                  animationDuration="1s"
                />
              </div>
            ) : (
              <i 
                className={statusIcon}
                style={{ 
                  fontSize: '4rem', 
                  color: statusMessage.severity === 'success' ? '#4caf50' : 
                         statusMessage.severity === 'error' ? '#f44336' : '#2196f3',
                  marginBottom: '20px',
                  display: 'block'
                }}
              ></i>
            )}

            {/* Title */}
            <h2 style={{ marginBottom: '10px' }}>
              {status?.posting_status === 'completed' ? 'All Done!' : 
               status?.posting_status === 'failed' ? 'Oops!' : 
               status?.posting_status === 'processing' || status?.posting_status === 'posting' ? 'Publishing Now' :
               'Payment Confirmed!'}
            </h2>

            {/* Tier timeframe message */}
            {status?.posting_status !== 'completed' && status?.posting_status !== 'failed' && 
             status?.posting_status !== 'processing' && status?.posting_status !== 'posting' && (
              <p style={{ 
                fontSize: '1.1rem', 
                color: '#4caf50',
                marginBottom: '20px',
                fontWeight: '600'
              }}>
                <i className="pi pi-calendar" style={{ marginRight: '8px' }}></i>
                Your post will go live {getTierTimeframe()}
              </p>
            )}

            {/* Status Message */}
            <Message 
              severity={statusMessage.severity} 
              text={statusMessage.text}
              style={{ marginBottom: '30px', justifyContent: 'center' }}
            />

            {/* Submission Details */}
            {status && (
              <div style={{ 
                textAlign: 'left', 
                backgroundColor: '#f5f5f5', 
                padding: '20px', 
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <h4 style={{ marginTop: 0, marginBottom: '15px' }}>Submission Details</h4>
                <div style={{ display: 'grid', gap: '10px' }}>
                  <div>
                    <strong>Name:</strong> {status.name}
                  </div>
                  <div>
                    <strong>School:</strong> {status.display_name || status.school}
                  </div>
                  <div>
                    <strong>Payment Status:</strong> 
                    <span style={{ 
                      marginLeft: '10px',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      backgroundColor: 
                        status.payment_status === 'paid' ? '#4caf50' : 
                        status.payment_status === 'promo' ? '#10b981' : 
                        '#ff9800',
                      color: 'white',
                      fontSize: '0.85em'
                    }}>
                      {status.payment_status === 'promo' ? 'Promo Code' : status.payment_status || 'pending'}
                    </span>
                  </div>
                  <div>
                    <strong>Posting Status:</strong> 
                    <span style={{ 
                      marginLeft: '10px',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      backgroundColor: 
                        status.posting_status === 'completed' ? '#4caf50' :
                        status.posting_status === 'failed' ? '#f44336' :
                        '#2196f3',
                      color: 'white',
                      fontSize: '0.85em'
                    }}>
                      {status.posting_status || 'not_queued'}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
              {status?.posting_status === 'completed' && (
                <>
                  {status.username && (
                    <Button 
                      label="View on Instagram" 
                      icon="pi pi-external-link"
                      onClick={() => window.open(`https://www.instagram.com/${status.username}/`, '_blank')}
                      className="p-button-success"
                    />
                  )}
                  <Button 
                    label="Submit Another" 
                    icon="pi pi-plus"
                    onClick={() => navigate('/')}
                  />
                </>
              )}
              
              {status?.posting_status === 'failed' && (
                <Button 
                  label="DM @UniPostings" 
                  icon="pi pi-instagram"
                  onClick={() => window.open('https://www.instagram.com/unipostings/', '_blank')}
                  className="p-button-help"
                />
              )}

              <Button 
                label="Go Home" 
                icon="pi pi-home"
                onClick={() => navigate('/')}
                className="p-button-secondary"
              />
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PostingStatusPage;

